/**
 * Auth Context for Admin PWA
 *
 * The account is the ONE PWA restaurant-account sign-in (lib/pwaAccount,
 * components/pwa/PwaLogin: password + 2FA code, or Google). Admin pages read
 * the token as `admin_token`, so it is mirrored there while signed in.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAccount, signOutAccount, accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

function mirror(account) {
  if (account) {
    localStorage.setItem('admin_token', account.token);
    localStorage.setItem('restaurant_id', String(account.restaurant.id));
  } else {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('restaurant_id');
  }
}

export function AuthProvider({ children }) {
  const account = useAccount();
  const token = account?.token || null;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);

  // keep admin_token in step with the account (before any page reads it)
  mirror(account);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/merchant/profile`, { headers: { ...accountHeaders() } })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          setUser(await res.json());
        } else if (res.status === 401) {
          mirror(null);
          signOutAccount('Your session ended. Please sign in again.');
        }
      })
      .catch((err) => console.error('Auth fetch error:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const logout = () => {
    mirror(null);
    signOutAccount();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
